import React from 'react';
import { Box, Button, Typography, Container } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useWebsiteSettings } from '../contexts/WebsiteSettingsContext';

export type FeatureSettings = {
  enabled?: boolean;
  title?: string;
  subtitle?: string;
  image?: string;
  ctaText?: string;
  ctaUrl?: string;
  // Appearance
  useGradient?: boolean;
  bgColor?: string;          // default background when not using gradient
  gradientFrom?: string;     // left/top color
  gradientTo?: string;       // right/bottom color
  textColor?: string;        // text color override
  buttonBg?: string;         // CTA background
  buttonText?: string;       // CTA text color
};

interface Props {
  feature: FeatureSettings | undefined;
  resolvePublicUrl: (p?: string) => string | undefined;
}

const FeaturedSection: React.FC<Props> = ({ feature, resolvePublicUrl }) => {
  // Hooks must run unconditionally
  const { settings } = useWebsiteSettings();
  const layout = (settings?.layout || {}) as any;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const maxWidth = !isMobile && typeof layout.container === 'string' && layout.container !== 'false' ? (layout.container as any) : false;
  const px = !isMobile && typeof layout.contentPaddingX === 'number' ? layout.contentPaddingX : 0;
  if (!feature || feature.enabled === false) return null;
  const img = feature.image ? resolvePublicUrl(feature.image) : undefined;

  // Colors with sensible defaults
  const useGradient = feature.useGradient === true;
  const bgColor = feature.bgColor || '#ffffff';
  const from = feature.gradientFrom || '#0ea5e9';
  const to = feature.gradientTo || '#22c55e';
  const textColor = feature.textColor || (useGradient ? '#ffffff' : '#0f172a');
  const btnBg = feature.buttonBg || (useGradient ? 'rgba(255,255,255,0.9)' : '#111827');
  const btnText = feature.buttonText || (useGradient ? '#0a0a0a' : '#ffffff');

  return (
    <Container maxWidth={maxWidth} sx={{ py: { xs: 3, md: 4 }, px: { xs: 1.5, md: px } }}>
      <Box sx={{
        position: 'relative',
        borderRadius: 4,
        overflow: 'hidden',
        boxShadow: '0 10px 24px rgba(2,6,23,0.08)',
        background: useGradient ? `linear-gradient(135deg, ${from} 0%, ${to} 100%)` : bgColor,
        color: textColor,
      }}>
        {img && (
          <Box component="img" src={img} alt={feature.title || 'Featured'} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: useGradient ? 0.12 : 0.08 }} />
        )}
        <Box sx={{ position: 'relative', px: { xs: 3, md: 6 }, py: { xs: 4, md: 6 } }}>
          {feature.title && (
            <Typography variant="h4" fontWeight={900} sx={{ mb: 1 }}>
              {feature.title}
            </Typography>
          )}
          {feature.subtitle && (
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
              {feature.subtitle}
            </Typography>
          )}
          {feature.ctaUrl && (
            <Button
              variant="contained"
              onClick={() => window.open(feature.ctaUrl!, '_blank', 'noopener,noreferrer')}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: 2,
                px: 3,
                py: 1.2,
                background: btnBg,
                color: btnText,
                boxShadow: useGradient ? '0 8px 20px rgba(255,255,255,0.25)' : '0 8px 20px rgba(2,6,23,0.15)',
                '&:hover': { filter: 'brightness(0.95)' },
              }}
            >
              {feature.ctaText || 'Jetzt entdecken'}
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
}
;

export default FeaturedSection;
